import type { Contact, ContactAttributeValue } from '../types/contacts.types';
import { toContactStringValue } from './contact-attributes';

const NATIVE_CONTACT_FIELDS: Record<string, keyof Contact> = {
  language: 'Language',
  source: 'Source',
  emailSubscriptionStatus: 'EmailSubscriptionStatus',
  smsSubscriptionStatus: 'SmsSubscriptionStatus',
};

const TITLE_CASE_COLUMN_IDS = new Set(['emailSubscriptionStatus', 'smsSubscriptionStatus']);

const getAttributeValue = (
  contact: Contact | undefined,
  propertyKey: string
): ContactAttributeValue | undefined =>
  contact?.Attributes?.find((entry) => entry?.PropertyKey === propertyKey) ?? undefined;

const titleCase = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const formatDateValue = (value: string): string => {
  const isoDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return isoDate?.[1] ?? value;
};

export const formatContactAttributeValue = (
  attribute: ContactAttributeValue | undefined
): string | undefined => {
  if (!attribute) return undefined;

  if (attribute.StringArrayValue && attribute.StringArrayValue.length > 0) {
    return attribute.StringArrayValue.filter(Boolean).join(', ') || undefined;
  }

  const stringValue = toContactStringValue(attribute.StringValue);
  if (stringValue) return stringValue;

  if (attribute.NumberValue !== null && attribute.NumberValue !== undefined) {
    return String(attribute.NumberValue);
  }

  if (attribute.BoolValue !== null && attribute.BoolValue !== undefined) {
    return attribute.BoolValue ? 'Yes' : 'No';
  }

  if (attribute.DateValue) {
    return formatDateValue(attribute.DateValue);
  }

  return undefined;
};

export const getContactColumnDisplayValue = (
  columnId: string,
  contact: Contact | undefined
): string | undefined => {
  if (!contact) return undefined;

  const nativeField = NATIVE_CONTACT_FIELDS[columnId];
  if (nativeField) {
    const value = toContactStringValue(contact[nativeField]);
    if (!value) return undefined;
    return TITLE_CASE_COLUMN_IDS.has(columnId) ? titleCase(value) : value;
  }

  return formatContactAttributeValue(getAttributeValue(contact, columnId));
};
